"use client";

import React, { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@/context/AuthContext";
import type { SiteSettings } from "@/services/siteSettingsRepository";

interface ComingSoonGateProps {
  children: React.ReactNode;
  settings: SiteSettings;
}

function normalizeBackgroundImage(path: string): string {
  const trimmed = path.trim() || "/images/landing-page-banner-images/banner-002.webp";
  if (/^(https?:)?\/\//i.test(trimmed)) {
    return trimmed;
  }
  return trimmed.replace(/^\/public\//, "/");
}

function countdownParts(targetAt: string, now: number): { hours: string; minutes: string } {
  const targetTime = new Date(targetAt).getTime();
  const safeTargetTime = Number.isFinite(targetTime)
    ? targetTime
    : new Date("2026-07-11T18:00:00+10:00").getTime();
  const remainingMs = Math.max(0, safeTargetTime - now);
  const totalMinutes = Math.ceil(remainingMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return {
    hours: String(hours).padStart(2, "0"),
    minutes: String(minutes).padStart(2, "0"),
  };
}

export default function ComingSoonGate({ children, settings }: ComingSoonGateProps) {
  const { permissions, isLoading, login } = useAuth();
  const pathname = usePathname();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!settings.comingSoonEnabled) {
      return;
    }

    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [settings.comingSoonEnabled]);

  const canBypassComingSoon = permissions.canManageSiteSettings();

  const isPublicServiceRoute = pathname?.startsWith("/auth/") || pathname === "/unsubscribe";

  if (!settings.comingSoonEnabled || canBypassComingSoon || isPublicServiceRoute) {
    return <>{children}</>;
  }

  const countdown = countdownParts(settings.comingSoonTargetAt, now);
  const backgroundImage = normalizeBackgroundImage(settings.comingSoonBackgroundImage);
  const isCheckingAccess = isLoading;

  return (
    <main
      className="coming-soon-screen"
      style={{ backgroundImage: `url("${backgroundImage}")` }}
      aria-label="Coming soon"
    >
      <div className="coming-soon-scrim" />
      <section className="coming-soon-content">
        <p className="coming-soon-kicker">Axiom Manifold</p>
        <h1 className="coming-soon-title">Coming Soon</h1>
        <p className="coming-soon-message">{settings.comingSoonMessage}</p>

        <div className="coming-soon-countdown" aria-label={`${countdown.hours} hours and ${countdown.minutes} minutes`}>
          <span>{countdown.hours}</span>
          <span className="coming-soon-countdown-separator">:</span>
          <span>{countdown.minutes}</span>
        </div>
        <p className="coming-soon-countdown-label">Hours&nbsp;&nbsp;/&nbsp;&nbsp;Minutes</p>

        <button type="button" onClick={login} className="coming-soon-access-button">
          {isCheckingAccess ? "Verifying access..." : "Staff access"}
        </button>
      </section>
    </main>
  );
}
